import { AppLink } from "@base/ui/AppLink";
import { Image } from "@base/ui/Image";
import { Button } from "@buildblockinc/buildblock-design-system";
import { useQuery } from "@tanstack/react-query";
import { ImageOff } from "lucide-react";
import { projectsQueryOptions } from "../model/useProjectsQuery";

const SKELETON_COUNT = 6;
const GRID_CLASS = "grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 xl:grid-cols-4";

export function ProjectsGrid() {
  const { data, isLoading, isError, refetch } = useQuery(projectsQueryOptions());

  if (isLoading) {
    return (
      <ul className={GRID_CLASS} aria-busy="true">
        {Array.from({ length: SKELETON_COUNT }, (_, i) => `project-skeleton-${i}`).map((key) => (
          <li key={key}>
            <div className="w-full aspect-[16/9] rounded-8 skeleton-shimmer" />
            <div className="mt-2 w-2/3 h-4 rounded-2 skeleton-shimmer" />
          </li>
        ))}
      </ul>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col gap-3 items-center py-10 text-center" role="alert">
        <p className="text-body3 text-gray-700">Failed to load projects.</p>
        <Button type="button" variant="outline" color="secondary" size="small" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  if (data.projects.length === 0) {
    return <p className="py-10 text-center text-body3 text-gray-700">You haven't joined any projects yet.</p>;
  }

  return (
    <ul className={GRID_CLASS}>
      {data.projects.map((project) => {
        const projectId = String(project.id);
        return (
          <li key={projectId} className="min-w-0">
            <AppLink
              to="/projects/$projectId"
              params={{ projectId }}
              className="block overflow-hidden bg-white rounded-8 border border-gray-200 hover:border-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 md:shadow-box md:transition-shadow"
            >
              {project.thumbnail_image_url ? (
                <Image
                  src={project.thumbnail_image_url}
                  alt=""
                  className="object-cover w-full aspect-[16/9]"
                  aspectRatio="16/9"
                />
              ) : (
                <div className="flex justify-center items-center w-full bg-gray-100 aspect-[16/9]">
                  <ImageOff className="w-6 h-6 text-gray-300" aria-hidden="true" />
                </div>
              )}
              <p className="px-3 py-3 truncate text-body3 text-gray-1000" title={project.name}>
                {project.name}
              </p>
            </AppLink>
          </li>
        );
      })}
    </ul>
  );
}
